import React, { useEffect, useState } from 'react';
import Topbar from '../components/Topbar';
import { supabase } from '../lib/supabase';
import { Star, Trash2, Mail, Hash, Clock, Search, Filter } from 'lucide-react';
import { useToast } from '../components/ui/Feedback';
import { friendlyError } from "../lib/errorMessage";

export function Reviews() {
  const [reviews, setReviews] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    supabase.from('reviews').select('*, profiles(full_name)')
      .order('created_at', { ascending: false })
      .then(({ data }) => { setReviews(data ?? []); setLoading(false); });
  }, []);

  async function deleteReview(id: string) {
    if (!window.confirm('Delete this review?')) return;
    // reviews had no DELETE policy before migration 0011, and this discarded
    // the error and dropped the row locally anyway — so the button looked
    // like it worked right up until you reloaded.
    const { error } = await supabase.from('reviews').delete().eq('id', id);
    if (error) { toast.failure(friendlyError(error, "Something went wrong. Please try again.")); return; }
    toast.success('Review deleted');
    setReviews(r => r.filter(x => x.id !== id));
  }

  return (
    <div>
      <Topbar title="Reviews" />
      <div style={{ padding: '24px 28px' }}>
        <div className="card">
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Customer Reviews</div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Rating</th>
                  <th>Review</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.profiles?.full_name ?? '—'}</td>
                    <td>{r.product_name ?? r.product_id ?? '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 2 }}>
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} size={13} fill={s <= r.rating ? '#f7b731' : 'none'} color={s <= r.rating ? '#f7b731' : '#ddd'} />
                        ))}
                        <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>{r.rating}/5</span>
                      </div>
                    </td>
                    <td style={{ maxWidth: 280 }}>
                      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {r.comment?.length > 120 ? r.comment.slice(0, 120) + '...' : r.comment ?? '—'}
                      </div>
                    </td>
                    <td>{new Date(r.created_at).toLocaleDateString('en-GB')}</td>
                    <td>
                      <button className="btn btn-sm btn-danger" style={{ border: 'none' }} onClick={() => deleteReview(r.id)}>
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                ))}
                {reviews.length === 0 && !loading && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>No reviews yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const REASON_COLORS: Record<string, { bg: string; color: string }> = {
  'Order Issue':        { bg: '#FEF3C7', color: '#92400E' },
  'Product Question':   { bg: '#EDE9FE', color: '#5B21B6' },
  'Wholesale Enquiry':  { bg: '#DBEAFE', color: '#1E40AF' },
  'Delivery Support':   { bg: '#D1FAE5', color: '#065F46' },
  'Returns & Refunds':  { bg: '#FFE4E6', color: '#9F1239' },
  'Other':              { bg: '#F3F4F6', color: '#374151' },
};

type Contact = {
  id: string;
  created_at: string;
  reason: string;
  full_name: string;
  email: string;
  order_number: string | null;
  message: string;
  user_id: string | null;
};

type FilterReason = 'All' | string;

export function Complaints() {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterReason, setFilterReason] = useState<FilterReason>('All');

  useEffect(() => {
    supabase
      .from('contacts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(200)
      .then(({ data, error }) => {
        if (!error) setContacts(data ?? []);
        console.log(error)
        setLoading(false);
      });
  }, []);

  const reasons = ['All', ...Array.from(new Set(contacts.map(c => c.reason).filter(Boolean)))];

  const filtered = contacts.filter(c => {
    const matchesReason = filterReason === 'All' || c.reason === filterReason;
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      c.full_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.message?.toLowerCase().includes(q) ||
      c.order_number?.toLowerCase().includes(q);
    return matchesReason && matchesSearch;
  });

  return (
    <div>
      <Topbar title="Support" />
      <div style={{ padding: '24px 28px' }}>

        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Total', value: contacts.length },
            { label: 'This week', value: contacts.filter(c => new Date(c.created_at) > new Date(Date.now() - 7 * 86400000)).length },
            { label: 'With order #', value: contacts.filter(c => c.order_number).length },
            { label: 'Linked users', value: contacts.filter(c => c.user_id).length },
          ].map(({ label, value }) => (
            <div key={label} style={{ background: 'var(--surface-soft, #f9f9f9)', borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>{value}</div>
            </div>
          ))}
        </div>

        <div className="card">
          {/* Header + filters */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 800, fontSize: 16 }}>Contact Submissions</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                Messages submitted via the website contact form
              </div>
            </div>
            <span className="badge badge-primary">{filtered.length} shown</span>
          </div>

          {/* Search + reason filter */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
              <input
                type="text"
                placeholder="Search by name, email, message…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  paddingLeft: 32, paddingRight: 12, paddingTop: 8, paddingBottom: 8,
                  border: '1px solid var(--border)', borderRadius: 8,
                  fontSize: 13, background: 'var(--surface)', color: 'var(--text-primary)',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              <Filter size={14} style={{ color: 'var(--text-muted)' }} />
              {reasons.map(r => (
                <button
                  key={r}
                  onClick={() => setFilterReason(r)}
                  style={{
                    padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    border: filterReason === r ? '1.5px solid var(--primary)' : '1px solid var(--border)',
                    background: filterReason === r ? 'var(--primary-light)' : 'transparent',
                    color: filterReason === r ? 'var(--primary)' : 'var(--text-muted)',
                    transition: 'all 0.15s',
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* Submissions list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {loading && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 48 }}>Loading…</div>
            )}
            {!loading && filtered.length === 0 && (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 48 }}>No submissions found</div>
            )}
            {filtered.map(contact => {
              const badge = REASON_COLORS[contact.reason] ?? REASON_COLORS['Other'];
              const initials = contact.full_name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase() ?? '?';
              return (
                <div
                  key={contact.id}
                  style={{ border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px', background: '#fff' }}
                >
                  {/* Row 1: avatar + name + email + date */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: 'var(--primary-light)', color: 'var(--primary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 13, flexShrink: 0,
                    }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>{contact.full_name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Mail size={11} />
                        {contact.email}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                      {/* Reason badge */}
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                        background: badge.bg, color: badge.color,
                      }}>
                        {contact.reason}
                      </span>
                      {/* Date */}
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={11} />
                        {contact.created_at ? new Date(contact.created_at).toLocaleString('en-GB') : '—'}
                      </div>
                    </div>
                  </div>

                  {/* Row 2: message */}
                  <div style={{
                    color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.65,
                    background: 'var(--surface-soft, #f9f9f9)', borderRadius: 8,
                    padding: '10px 12px',
                  }}>
                    {contact.message}
                  </div>

                  {/* Row 3: order number (if present) */}
                  {contact.order_number && (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)' }}>
                      <Hash size={12} />
                      Order: <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{contact.order_number}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

type ZoraConversation = {
  id: string;
  user_id: string;
  profiles?: { full_name?: string };
  created_at?: string;
  messages: any[];
};

const messageText = (message: any) => message.content ?? message.message ?? message.text ?? message.prompt ?? '—';
const messageRole = (message: any) => message.role ?? message.sender ?? (message.response ? 'assistant' : 'user');

export function ZoraData() {
  const [conversations, setConversations] = useState<ZoraConversation[]>([]);
  const [usageRows, setUsageRows] = useState<any[]>([]);
  const [topQuestions, setTopQuestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ZoraConversation | null>(null);

  useEffect(() => {
    Promise.all([
      supabase.from('zora_messages').select('*, profiles(full_name)').order('created_at', { ascending: false }).limit(200),
      supabase.from('zora_usage').select('*, profiles(full_name)').order('created_at', { ascending: false }).limit(20),
      supabase.from('top_questions').select('*').limit(20),
    ]).then(([messagesRes, usageRes, questionsRes]) => {
        const data = messagesRes.data ?? [];
        const grouped = (data ?? []).reduce<Record<string, ZoraConversation>>((acc, row: any) => {
          const key = row.user_id ?? row.id;
          if (!acc[key]) {
            acc[key] = {
              id: key,
              user_id: row.user_id,
              profiles: row.profiles,
              created_at: row.created_at,
              messages: [],
            };
          }
          acc[key].messages.push(row);
          if (row.created_at && (!acc[key].created_at || row.created_at > acc[key].created_at!)) {
            acc[key].created_at = row.created_at;
          }
          return acc;
        }, {});
        const nextConversations = Object.values(grouped);
        setConversations(nextConversations);
        setSelected(nextConversations[0] ?? null);
        setUsageRows(usageRes.data ?? []);
        setTopQuestions(questionsRes.data ?? []);
        setLoading(false);
      });
  }, []);

  return (
    <div>
      <Topbar title="Zora AI Data" />
      <div style={{ padding: '24px 28px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
          {/* List */}
          <div className="card" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Users with Zora Messages ({conversations.length})</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {conversations.map(c => (
                <button key={c.id} onClick={() => setSelected(c)} style={{
                  padding: '12px 14px', border: `1px solid ${selected?.id === c.id ? 'var(--primary)' : 'var(--border)'}`,
                  borderRadius: 10, background: selected?.id === c.id ? 'var(--primary-light)' : '#fff',
                  cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 3 }}>
                    {c.profiles?.full_name ?? 'Unknown User'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : '—'} · {c.messages.length} messages
                  </div>
                </button>
              ))}
              {conversations.length === 0 && !loading && (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>No Zora messages yet</div>
              )}
            </div>
          </div>

          {/* Detail */}
          <div className="card" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            {selected ? (
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>
                  {selected.profiles?.full_name ?? 'Unknown'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 }}>
                  {selected.created_at ? new Date(selected.created_at).toLocaleString('en-GB') : 'No timestamp'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {selected.messages.map((msg: any) => {
                    const role = messageRole(msg);
                    const isUser = role === 'user';
                    return (
                      <div key={msg.id} style={{
                        padding: '10px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.6,
                        background: isUser ? 'var(--bg)' : 'var(--primary-light)',
                        color: isUser ? 'var(--text-primary)' : 'var(--primary-dark)',
                        alignSelf: isUser ? 'flex-start' : 'flex-end',
                        maxWidth: '85%',
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, marginBottom: 4, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {isUser ? 'User' : 'Zora'}
                        </div>
                        {messageText(msg)}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--text-muted)', fontSize: 13 }}>
                Select a user to view Zora messages
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          <div className="card">
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 16 }}>Zora Usage</div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Usage</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {usageRows.map(row => (
                    <tr key={row.id}>
                      <td>{row.profiles?.full_name ?? row.user_id ?? '—'}</td>
                      <td>{row.count ?? row.usage_count ?? row.messages_count ?? row.total ?? '—'}</td>
                      <td>{row.created_at ? new Date(row.created_at).toLocaleDateString('en-GB') : '—'}</td>
                    </tr>
                  ))}
                  {usageRows.length === 0 && !loading && (
                    <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>No usage rows yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 16 }}>Top Questions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {topQuestions.map((row, index) => (
                <div key={row.id ?? index} style={{ padding: 12, border: '1px solid var(--border)', borderRadius: 12, background: '#fff' }}>
                  <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>
                    {row.question ?? row.prompt ?? row.content ?? row.text ?? 'Question'}
                  </div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
                    Asked {row.count ?? row.frequency ?? row.uses ?? row.total ?? '—'} times
                  </div>
                </div>
              ))}
              {topQuestions.length === 0 && !loading && (
                <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>No top questions yet</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
